import React from 'react';
import styled, { css } from 'styled-components';
import CheckCircleIcon from '../../../../../generic/icons/IconLib/CheckCircleIcon';
import HashIcon from '../../../../../generic/icons/IconLib/HashIcon';
import InvoiceIcon from '../../../../../generic/icons/IconLib/InvoiceIcon';
import PalletIcon from '../../../../../generic/icons/IconLib/PalletIcon';
import PaperclipIcon from '../../../../../generic/icons/IconLib/PaperclipIcon';
import PencilIcon from '../../../../../generic/icons/IconLib/PencilIcon';
import PrintIcon from '../../../../../generic/icons/IconLib/PrintIcon';
import { Mono } from '../../../../../generic/ui/Mono';
import { DriversTag } from './DriversCardParts';
import { IOrderItem } from '../../../../../modules/API/Drivers/models/kavData';
import StatusReturningBadge from './StatusReturningBadge';
import { aggregateOrders } from '../helpers/aggregate';
import BasketIcon from '../../../../../generic/icons/IconLib/BasketIcon';

// Тип сущности 1 — теуда; заказы без неё адресуются по OrderID.
const PHOTO_ENTITY_TYPE = 1;

type TileEdge = 'none' | 'success' | 'danger';

export interface OrderTileProps {
    order: IOrderItem;
    groupColor?: string;
    onOpenPallets: (order: IOrderItem) => void;
    onPrint: (deliveryNum: number, deliverySub: number) => void;
    onPhoto: (entityType: number, entityId: number) => void;
    hasPhoto: boolean;
}

const OrderTile: React.FC<OrderTileProps> = ({
    order,
    groupColor,
    onOpenPallets,
    onPrint,
    onPhoto,
    hasPhoto,
}) => {
    const isTeuda = order.Delivery_Num !== 0;
    const isDelivered = order.LastAction?.Action === 1;
    const isFailed = order.LastAction?.Action > 1;
    const edge: TileEdge = isDelivered ? 'success' : isFailed ? 'danger' : 'none';
    const photoEntityId = order.Delivery_Num || order.OrderID;
    const { pallets } = aggregateOrders([order]);
    const group = order.GroupDesc?.trim();
    const note = order.DistributNote?.trim();

    const handlePrint = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isTeuda) return;
        onPrint(+order.Delivery_Num, +order.Delivery_Sub);
    };

    const handlePhoto = (e: React.MouseEvent) => {
        e.stopPropagation();
        onPhoto(PHOTO_ENTITY_TYPE, photoEntityId);
    };

    return (
        <Tile
            $edge={edge}
            role='button'
            tabIndex={0}
            onClick={() => onOpenPallets(order)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpenPallets(order);
                }
            }}
        >
            <HeadLine>
                <IconSlot>{isTeuda ? <InvoiceIcon size={13} /> : <BasketIcon size={13} />}</IconSlot>
                <Id>{isTeuda ? order.Delivery_Num : order.OrderID}</Id>
                {group && (
                    <DriversTag variant='tinted' color={groupColor}>
                        {group}
                    </DriversTag>
                )}
            </HeadLine>

            <Line>
                <IconSlot>
                    <PalletIcon size={12} />
                </IconSlot>
                <Meta>{pallets.toFixed(2)}</Meta>
            </Line>

            {note && (
                <NoteLine>
                    <PencilIcon size={12} />
                    <NoteText>{note}</NoteText>
                </NoteLine>
            )}

            {order.IsProblem && <StatusReturningBadge status={order.DriverStatus} />}

            <Actions>
                {isTeuda && (
                    <TileButton
                        type='button'
                        onClick={handlePrint}
                        aria-label='הדפסת תעודה'
                        title='הדפסת תעודה'
                    >
                        <PrintIcon size={14} />
                    </TileButton>
                )}
                <TileButton
                    type='button'
                    $active={hasPhoto}
                    onClick={handlePhoto}
                    aria-label='העלאת תמונה או הערה'
                    title='העלאת תמונה או הערה'
                >
                    {hasPhoto ? <CheckCircleIcon size={14} /> : <PaperclipIcon size={14} />}
                </TileButton>
            </Actions>
        </Tile>
    );
};

export default OrderTile;

/** Состояние тайла — пастельной заливкой из палитры, без солидных акцентов. */
const edgeFill = {
    none: null,
    success: 'successSoft',
    danger: 'dangerSoft',
} as const;

const Tile = styled.div<{ $edge: TileEdge }>`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
    padding: 0.4375rem 0.5rem;
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: ${({ theme }) => theme.radii.md};
    background: ${({ theme }) => theme.colors.bg};
    cursor: pointer;
    transition: background ${({ theme }) => theme.motion.fast} ease;

    ${({ $edge, theme }) => {
        const fill = edgeFill[$edge];
        return (
            fill &&
            css`
                background: ${theme.colors[fill]};
                border-color: ${theme.colors[fill]};
            `
        );
    }}

    ${({ $edge, theme }) =>
        $edge === 'none' &&
        css`
            @media (hover: hover) and (pointer: fine) {
                &:hover {
                    background: ${theme.colors.line2};
                }
            }
        `}

    &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.colors.accent};
        outline-offset: -2px;
    }
`;

const Line = styled.div`
    display: flex;
    align-items: center;
    gap: 0.25rem;
    min-width: 0;
`;

const HeadLine = styled(Line)`
    flex-wrap: wrap;
    row-gap: 0.25rem;
`;


const IconSlot = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink3};
    flex-shrink: 0;
`;

const Id = styled(Mono)`
    font-size: ${({ theme }) => theme.text.md};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`;

const Meta = styled(Mono)`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink3};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

const NoteLine = styled.div`
    display: flex;
    align-items: center;
    gap: 0.25rem;
    min-width: 0;
    color: ${({ theme }) => theme.colors.danger};
`;

const NoteText = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.xs};
    color: ${({ theme }) => theme.colors.danger};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.xs};
    }
`;

const Actions = styled.div`
    display: flex;
    gap: 0.25rem;
    margin-top: 0.125rem;
`;

/**
 * Визуально кнопка 1.625rem, но ::after расширяет зону нажатия до 2.75rem —
 * иначе на телефоне промахнуться по кубику проще, чем попасть по кнопке.
 */
const TileButton = styled.button<{ $active?: boolean }>`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.625rem;
    height: 1.625rem;
    padding: 0;
    flex-shrink: 0;
    border-radius: ${({ theme }) => theme.radii.sm};
    border: 1px solid
        ${({ theme, $active }) => ($active ? theme.colors.accent : theme.colors.line)};
    background: ${({ theme, $active }) =>
        $active ? theme.colors.accent : theme.colors.line2};
    color: ${({ theme, $active }) => ($active ? theme.colors.ink : theme.colors.ink3)};
    cursor: pointer;
    transition: all ${({ theme }) => theme.motion.fast} ease;

    &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 2.75rem;
        height: 2.75rem;
        transform: translate(-50%, -50%);
    }

    &:hover {
        background: ${({ theme }) => theme.colors.accent};
        border-color: ${({ theme }) => theme.colors.accent};
        color: ${({ theme }) => theme.colors.ink};
    }

    &:active {
        transform: translateY(1px);
    }
`;
